// src/utils/driverDetailsCache.js

const DRIVER_CACHE_PREFIX =
  "loadevo.driver-details.v2";

const LEGACY_CACHE_PREFIX =
  "driver:";

/*
 * O cache pode continuar a ser apresentado mesmo estando
 * ligeiramente desatualizado, porque o EditDriver fará sempre
 * uma atualização silenciosa através do backend.
 *
 * Depois de 7 dias, deixamos de confiar no conteúdo.
 */
const DRIVER_CACHE_MAX_AGE =
  7 *
  24 *
  60 *
  60 *
  1000;

const memoryCache =
  new Map();

function getDriverCacheKey(
  driverId
) {
  return `${DRIVER_CACHE_PREFIX}:${driverId}`;
}

function getLegacyDriverCacheKey(
  driverId
) {
  return `${LEGACY_CACHE_PREFIX}${driverId}`;
}

function isBrowser() {
  return (
    typeof window !==
    "undefined"
  );
}

function parseStoredEntry(
  raw
) {
  if (!raw) {
    return null;
  }

  try {
    const parsed =
      JSON.parse(
        raw
      );

    /*
     * Formato atual:
     *
     * {
     *   savedAt: 123456789,
     *   data: {...}
     * }
     */
    if (
      parsed &&
      typeof parsed ===
        "object" &&
      parsed.data &&
      typeof parsed.data ===
        "object"
    ) {
      return {
        savedAt:
          Number(
            parsed.savedAt
          ) || 0,

        data:
          parsed.data,
      };
    }

    /*
     * Compatibilidade com o cache antigo,
     * que guardava diretamente o motorista.
     */
    if (
      parsed &&
      typeof parsed ===
        "object"
    ) {
      return {
        savedAt:
          Date.now(),

        data:
          parsed,
      };
    }
  } catch {
    return null;
  }

  return null;
}

function isUsableEntry(
  entry
) {
  if (
    !entry ||
    !entry.data
  ) {
    return false;
  }

  /*
   * Entradas antigas sem savedAt são aceites
   * e serão migradas para o novo formato.
   */
  if (
    !entry.savedAt
  ) {
    return true;
  }

  return (
    Date.now() -
      entry.savedAt <
    DRIVER_CACHE_MAX_AGE
  );
}

function removeStorageItem(
  storage,
  key
) {
  try {
    storage.removeItem(
      key
    );
  } catch {
    // Storage indisponível ou bloqueado.
  }
}

function readStorageEntry(
  storage,
  key
) {
  try {
    return parseStoredEntry(
      storage.getItem(
        key
      )
    );
  } catch {
    return null;
  }
}

function isMeaningfulValue(
  value
) {
  if (
    value === true ||
    value === false
  ) {
    return true;
  }

  if (
    typeof value ===
    "number"
  ) {
    return true;
  }

  if (
    typeof value ===
    "string"
  ) {
    return (
      value.trim() !==
      ""
    );
  }

  if (
    Array.isArray(
      value
    )
  ) {
    return (
      value.length >
      0
    );
  }

  if (
    value &&
    typeof value ===
      "object"
  ) {
    return (
      Object.keys(
        value
      ).length >
      0
    );
  }

  return false;
}

/*
 * Usa-se para juntar dados parciais vindos da lista
 * com dados completos que já estejam guardados.
 *
 * Valores vazios da lista não apagam os valores completos.
 */
export function mergeDriverPreviewData(
  current,
  incoming
) {
  const result = {
    ...(
      current ||
      {}
    ),
  };

  Object.entries(
    incoming ||
      {}
  ).forEach(
    ([
      key,
      value,
    ]) => {
      if (
        isMeaningfulValue(
          value
        )
      ) {
        result[key] =
          value;
      }
    }
  );

  return result;
}

export function readDriverDetailsCache(
  driverId
) {
  if (
    !driverId
  ) {
    return null;
  }

  const id =
    String(
      driverId
    );

  if (
    memoryCache.has(
      id
    )
  ) {
    return (
      memoryCache.get(
        id
      ) || null
    );
  }

  if (
    !isBrowser()
  ) {
    return null;
  }

  const cacheKey =
    getDriverCacheKey(
      id
    );

  /*
   * Primeiro localStorage, porque persiste
   * entre sessões e recarregamentos.
   */
  const localEntry =
    readStorageEntry(
      window.localStorage,
      cacheKey
    );

  if (
    isUsableEntry(
      localEntry
    )
  ) {
    memoryCache.set(
      id,
      localEntry.data
    );

    return localEntry.data;
  }

  if (
    localEntry
  ) {
    removeStorageItem(
      window.localStorage,
      cacheKey
    );
  }

  /*
   * Fallback para sessionStorage.
   */
  const sessionEntry =
    readStorageEntry(
      window.sessionStorage,
      cacheKey
    );

  if (
    isUsableEntry(
      sessionEntry
    )
  ) {
    memoryCache.set(
      id,
      sessionEntry.data
    );

    return sessionEntry.data;
  }

  if (
    sessionEntry
  ) {
    removeStorageItem(
      window.sessionStorage,
      cacheKey
    );
  }

  /*
   * Migração automática do formato antigo:
   *
   * driver:123
   */
  const legacyKey =
    getLegacyDriverCacheKey(
      id
    );

  const legacyEntry =
    readStorageEntry(
      window.sessionStorage,
      legacyKey
    );

  if (
    isUsableEntry(
      legacyEntry
    )
  ) {
    writeDriverDetailsCache(
      id,
      legacyEntry.data
    );

    removeStorageItem(
      window.sessionStorage,
      legacyKey
    );

    return legacyEntry.data;
  }

  return null;
}

/*
 * Escrita autoritativa.
 *
 * Deve ser usada depois de um GET individual
 * ou depois de guardar o motorista.
 */
export function writeDriverDetailsCache(
  driverId,
  driver
) {
  if (
    !driverId ||
    !driver ||
    typeof driver !==
      "object"
  ) {
    return;
  }

  const id =
    String(
      driverId
    );

  const data = {
    ...driver,
  };

  const entry = {
    savedAt:
      Date.now(),

    data,
  };

  memoryCache.set(
    id,
    data
  );

  if (
    !isBrowser()
  ) {
    return;
  }

  const serialized =
    JSON.stringify(
      entry
    );

  const cacheKey =
    getDriverCacheKey(
      id
    );

  try {
    window.localStorage.setItem(
      cacheKey,
      serialized
    );
  } catch {
    // localStorage indisponível ou cheio.
  }

  try {
    window.sessionStorage.setItem(
      cacheKey,
      serialized
    );
  } catch {
    // sessionStorage indisponível ou cheio.
  }
}

/*
 * Escrita não destrutiva.
 *
 * Ideal para a página Drivers, pois a lista pode conter
 * apenas parte das informações do motorista.
 */
export function primeDriverDetailsCache(
  driverId,
  partialDriver
) {
  if (
    !driverId ||
    !partialDriver
  ) {
    return;
  }

  const current =
    readDriverDetailsCache(
      driverId
    );

  const merged =
    mergeDriverPreviewData(
      current,
      partialDriver
    );

  writeDriverDetailsCache(
    driverId,
    merged
  );
}

export function removeDriverDetailsCache(
  driverId
) {
  if (
    !driverId
  ) {
    return;
  }

  const id =
    String(
      driverId
    );

  memoryCache.delete(
    id
  );

  if (
    !isBrowser()
  ) {
    return;
  }

  const cacheKey =
    getDriverCacheKey(
      id
    );

  removeStorageItem(
    window.localStorage,
    cacheKey
  );

  removeStorageItem(
    window.sessionStorage,
    cacheKey
  );

  removeStorageItem(
    window.sessionStorage,
    getLegacyDriverCacheKey(
      id
    )
  );
}